import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {useNavigate} from 'react-router';
import ReadbookAuthorTooltip from './ReadbookAuthorTooltip';

/**
 * ReadbookItemStack
 * Renders a readbook item with clickable points. Interaction moved to ListItems.
 * Props:
 * - item: { id, title, points: string[], published }
 * - onClick: optional click handler for the whole item (kept but no interactive styling)
 * - activeItemId: optional route param indicating which point uid is active
 * - isActive: legacy boolean; if activeItemId not provided, this may be used to mark whole item active
 */
const ReadbookItemStack = ({item, onClick, activeItemId, isActive: legacyActive}) => {
    const navigate = useNavigate();

    // Safely parse points: item.points may be a JSON string or already an array
    let points = [];
    try {
        if (typeof item?.points === 'string') {
            points = JSON.parse(item.points) || [];
        } else if (Array.isArray(item?.points)) {
            points = item.points;
        }
    } catch (e) {
        console.warn('Failed to parse item.points', e);
        points = [];
    }

    return (
        <Stack
            bgcolor={'white'}
            sx={{
                // Remove interactive pointer/hover styles from parent; interactions are on list items now
                outline: legacyActive ? '2px solid #3f51b5' : 'none',
                backgroundColor: legacyActive ? '#eff1f9' : 'white'
            }}
        >
            <Stack spacing={1} p={2}>
                <Stack spacing={1}>
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                        {item.title}
                    </Typography>
                </Stack>

                <Stack p={2} pt={1} pb={1.5}
                       outline={'1px solid #e0e0e0'}
                >
                    <Typography variant="subtitle2" color="text.secondary">
                        Extracted from report:
                    </Typography>
                    <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                        {item.reportTitle}
                    </Typography>
                    <Stack direction={'row'} alignItems={'center'} spacing={1} mt={1}>
                        <ReadbookAuthorTooltip user={{
                            userDisplayName: item.userDisplayName,
                            displayName: item.userDisplayName,
                            headshot: item.headshot,
                            agency: item.agency,
                            office: item.office,
                            poc: item.poc,
                            contact: item.contact,
                            title: item.userTitle,
                            email: item.userEmail,
                            uid: item.userId,
                        }}/>
                    </Stack>
                </Stack>
            </Stack>

            <Stack>
                {Array.isArray(points) && points.length > 0 && (
                    <List disablePadding>
                        {points.map((p, i) => {
                            // support multiple shapes: [uid, text], {uid, text}, or string
                            let uid, text;
                            if (Array.isArray(p)) {
                                uid = p[0];
                                text = p[1];
                            } else if (p && typeof p === 'object') {
                                uid = p.uid || p.id || p[0];
                                text = p.text || p.title || String(p);
                            } else {
                                text = String(p);
                            }

                            const pointIsActive = activeItemId ? String(activeItemId) === String(uid) : false;

                            const handlePointClick = (e) => {
                                // prevent any parent handlers
                                e.stopPropagation();
                                if (uid) {
                                    // navigate to uid (adjust route as needed)
                                    navigate(String(uid));
                                } else if (onClick) {
                                    // fallback: if point has no uid, treat clicking point as clicking whole item
                                    onClick();
                                }
                            };

                            const handleKeyDown = (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handlePointClick(e);
                                }
                            };

                            return (
                                <ListItem
                                    key={i}
                                    disableGutters
                                    button={!!uid || !!onClick}
                                    onClick={handlePointClick}
                                    onKeyDown={handleKeyDown}
                                    sx={{
                                        cursor: uid || onClick ? 'pointer' : 'default',
                                        backgroundColor: pointIsActive ? '#eff1f9' : 'transparent',
                                        borderLeft: pointIsActive ? '4px solid' : undefined,
                                        borderLeftColor: pointIsActive ? 'primary.main' : undefined,
                                        px: 2
                                    }}
                                    aria-current={pointIsActive ? 'true' : undefined}
                                >
                                    <ListItemText primary={text}/>
                                </ListItem>
                            )
                        })}
                    </List>
                )}
            </Stack>
        </Stack>
    );
};

export default ReadbookItemStack;
